import { Briefcase, Sparkles, Users, Wrench } from "lucide-react";
import { AcademySectionHeader } from "@/components/academy/AcademyBrand";
import { Artwork } from "@/components/academy/Artwork";
import { useReveal } from "@/components/academy/teaser/useReveal";
import { TEASER_AUDIENCES, TEASER_TOPICS } from "@/lib/academy/teaser";
import { CONTAINER, SECTION } from "@/lib/academy/ui";

const AUDIENCE_ICONS = { new: Sparkles, build: Wrench, business: Briefcase, teams: Users };

const TopicCard = ({ topic: { id, title, body, art }, index }) => (
  <li style={{ "--i": index }} data-testid={`academy-teaser-topic-${id}`} className="teaser-card group academy-card overflow-hidden">
    <div className="relative h-28 border-b hairline bg-[#F7F8F9] text-[#160907]">
      <Artwork direction={art} className="absolute inset-0 w-full h-full opacity-80" />
      <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 border border-[rgba(22,9,7,0.22)] bg-white px-2 py-1 font-space-mono text-[9px] text-[#160907]"><span aria-hidden="true" className="academy-node !w-[5px] !h-[5px]" />In development</span>
      <span aria-hidden="true" className="absolute right-3 top-3 font-space-mono text-[10px] text-[#5F6168]">0{index + 1}</span>
    </div>
    <div className="p-6">
      <h3 className="font-display text-xl text-[#160907]">{title}</h3>
      <p className="mt-2 text-sm text-[#3D4049] leading-relaxed">{body}</p>
    </div>
  </li>
);

export const TeaserPreview = () => {
  const [ref, shown] = useReveal();
  return (
    <section id="whats-coming" data-testid="academy-teaser-preview" aria-labelledby="academy-preview-title" className={`${SECTION} bg-white`}>
      <div className={CONTAINER}>
        <AcademySectionHeader id="academy-preview" label="What's coming" title="Six Ways Into the Industry Behind AI" intro="Every lesson connects a real system to the people and businesses that run it, in plain language, with something practical to take away." />
        <ul ref={ref} className={`teaser-reveal ${shown ? "is-shown" : ""} grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4`}>
          {TEASER_TOPICS.map((topic, index) => <TopicCard key={topic.id} topic={topic} index={index} />)}
        </ul>
      </div>
    </section>
  );
};

export const TeaserAudience = () => {
  const [ref, shown] = useReveal();
  return (
    <section data-testid="academy-teaser-audience" aria-labelledby="academy-built-for-title" className={`${SECTION} bg-[#F7F8F9]`}>
      <div className={CONTAINER}>
        <AcademySectionHeader id="academy-built-for" label="Built for" title="Wherever You Start, There's a Way In" intro="DCYB Academy is being designed for newcomers and professionals alike, and for companies that want their people up to speed faster." />
        <ul ref={ref} className={`teaser-reveal ${shown ? "is-shown" : ""} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t hairline bg-white`}>
          {TEASER_AUDIENCES.map(({ id, title, body }, index) => {
            const Icon = AUDIENCE_ICONS[id];
            return (
              <li key={id} style={{ "--i": index }} data-testid={`academy-teaser-audience-${id}`} className="border-r border-b hairline p-7 flex flex-col gap-4 min-h-[210px] bg-gradient-to-b from-white to-[#F4F5F7]">
                <div className="flex items-center justify-between"><span className="font-space-mono text-[10px] text-[#5F6168]">0{index + 1}</span><span className="academy-tile w-11 h-11 flex items-center justify-center"><Icon size={18} aria-hidden="true" className="text-[#160907]" /></span></div>
                <h3 className="font-display text-lg lg:text-xl text-[#160907] leading-snug text-balance">{title}</h3>
                <p className="text-sm text-[#3D4049] leading-relaxed">{body}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
